// src/lib/api.ts — Stable Before Dev - 21-10-2025-V04 12:00 PM
import axios, { AxiosInstance, AxiosRequestConfig, AxiosHeaders } from "axios";

/* -------------------------------------------------------
 * قاعدة الـ API
 * ----------------------------------------------------- */
/* لكلّ متصفّح زارنا قبل 2026-10-09: قيمة «api_base» محفوظة من مرحلة ما قبل الدومين (http://<عنوان>:8000)
 * لم تعد تُقرأ — تُحذف مرّة واحدة كي لا يبقى أحد عالقًا عليها (المنفذ 8000 مغلق الآن). */
if (typeof window !== "undefined") {
  try { window.localStorage.removeItem("api_base"); } catch { /* ignore */ }
}

export function apiBase(): string {
  // مسار واحد عبر nginx (2026-10-09، HANDOFF «المنفذان 8000 و3000»): الخادم والواجهة على 127.0.0.1 وحده،
  // فلا يصل المتصفّح إلى :8000 مباشرةً أبدًا. حُذفت: فرع المنفذ 3000 ⇒ :8000، وفرع http ⇒ :8000،
  // وقراءة api_base من localStorage (وكاتبها الميّت setApiBaseRuntime) — كلّها لمرحلة ما قبل الدومين.

  // 1) تجاوز مقصود من .env (خادم تجريبيّ منفصل مثلًا) — فارغ في التشغيل العاديّ
  const env = (process.env.NEXT_PUBLIC_API_BASE || "").trim();
  if (env) return env.replace(/\/+$/, "");

  // 2) المتصفّح: الأصل نفسه + /_be (nginx يوجّهه إلى الخادم) — http أو https
  if (typeof window !== "undefined") return window.location.origin + "/_be";

  // 3) داخل خادم الواجهة (لا متصفّح): الخادم على السيرفر نفسه — 127.0.0.1 صراحةً لا localhost
  //    (قد تُترجَم إلى ::1 أوّلًا والخادم مربوط بـIPv4 وحده)
  return "http://127.0.0.1:8000";
}


// ** مُساعد صغير لاختيار المسار النهائي بشكل سليم (اختياري). */
export function apiPath(path: string): string {
  const base = apiBase();
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${base}${clean}`;
}



/* -------------------------------------------------------
 * كوكي
 * ----------------------------------------------------- */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  // نهرب كل محارف الـ RegExp الخاصة
  const safeName = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1");
  const m = document.cookie.match(new RegExp("(?:^|; )" + safeName + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

export function setCookie(name: string, value: string, days = 7) {
  if (typeof document === "undefined") return;
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "expires=" + d.toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
}

export function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

/* -------------------------------------------------------
 * توكن المصادقة (JWT/Bearer)
 * - نستخدم localStorage + Cookie "access_token"
 * ----------------------------------------------------- */
const TOKEN_KEY = "access_token";

/** احصل على التوكن من LS أولًا ثم الكوكي. */
export function getToken(): string | null {
  try {
    if (typeof localStorage !== "undefined") {
      const ls = localStorage.getItem(TOKEN_KEY);
      if (ls) return ls;
    }
  } catch { /* ignore */ }
  return getCookie(TOKEN_KEY);
}

/** خزّن التوكن في LS + Cookie. إن مرّرت null سيتم الحذف. */
export function setToken(token: string | null, cookieDays = 7) {
  try {
    if (typeof localStorage !== "undefined") {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    }
  } catch { /* ignore */ }
  if (token) setCookie(TOKEN_KEY, token, cookieDays);
  else deleteCookie(TOKEN_KEY);
}

/** احذف كل آثار التوكن. */
export function clearToken() {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch { /* ignore */ }
  deleteCookie(TOKEN_KEY);
}


// ** رؤوس المصادقة الجاهزة للإرسال. */
export function getAuthHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* -------------------------------------------------------
 * Axios instance افتراضي + تصدير افتراضي
 * ----------------------------------------------------- */
export const api: AxiosInstance = axios.create({
  baseURL: apiBase(),
  withCredentials: true, // نحتاج الكوكي HttpOnly
  headers: {
    Accept: "application/json",
  },
});

/** تحقّق هل لدى headers ترويسة Authorization معيّنة مسبقًا (تدعم AxiosHeaders). */
function hasAuthorizationHeader(h: any): boolean {
  if (!h) return false;
  // AxiosHeaders يملك get()
  if (typeof h.get === "function") {
    return !!h.get("Authorization");
  }
  // كائن عادي
  return "Authorization" in h;
}

/** أضف/ادمج رؤوسًا بأمان (تدعم AxiosHeaders). */
function mergeHeaders(target: any, extra: Record<string, string>) {
  if (!target) return extra;
  if (target instanceof AxiosHeaders || typeof target.set === "function") {
    for (const [k, v] of Object.entries(extra)) {
      target.set(k, v);
    }
    return target;
  }
  return Object.assign(target, extra);
}

// ضَمِّن Authorization تلقائيًا إن لم يكن مُرسلًا + زامن baseURL في كل طلب
api.interceptors.request.use((config) => {
  // أعِد تقييم baseURL في كلّ طلب (apiBase يقرأ أصل الصفحة الحاليّ)
  const currentBase = apiBase();
  if (currentBase) {
    // أزل الشرطات المائلة الختامية للتناسق
    const normalized = currentBase.replace(/\/+$/, "");
    if (config.baseURL !== normalized) {
      config.baseURL = normalized;
    }
  }

  // رؤوس
  if (!config.headers) config.headers = new AxiosHeaders();
  if (!hasAuthorizationHeader(config.headers)) {
    mergeHeaders(config.headers, getAuthHeaders());
  }

  // تأكد من withCredentials
  (config as AxiosRequestConfig).withCredentials = true;
  return config;
});

// ── Response interceptor: إعادة التوجيه عند 401/403 ──
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      // توكن منتهي أو غير صالح → تسجيل خروج + توجيه لصفحة الدخول
      if (typeof window !== "undefined") {
        clearToken();
        // تجنب حلقة لا نهائية: لا تُعيد التوجيه إذا كنا بالفعل في صفحة الدخول
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/** the token's iat/exp (seconds) — null when it cannot be read */
export function tokenTimes(token: string | null): { iat: number | null; exp: number } | null {
  try {
    const part = (token || "").split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=")));
    return typeof json.exp === "number" ? { iat: typeof json.iat === "number" ? json.iat : null, exp: json.exp } : null;
  } catch { return null; }
}

/** a display's session goes on while it works (owner 2026-11-03, «أ» — V5: a display nobody touches fell to the login
 *  page when its 12-hour token ended): past half its life, a new token from `POST /auth/refresh`. True when renewed */
export async function refreshTokenIfAging(now = Date.now() / 1000): Promise<boolean> {
  const t = tokenTimes(getToken());
  if (!t) return false;
  const life = t.iat ? t.exp - t.iat : 12 * 3600;
  if (t.exp - now > life / 2) return false;
  try {
    const { data } = await api.post("/auth/refresh");
    if (typeof data?.access_token === "string" && data.access_token) { setToken(data.access_token); return true; }
  } catch { /* the next check — an ended token is the login page's, as before */ }
  return false;
}

/* -------------------------------------------------------
 * مُساعد fetch JSON (اختياري)
 * ----------------------------------------------------- */
export async function jsonFetch<T = any>(
  path: string,
  init: RequestInit & { json?: any } = {}
): Promise<{ ok: boolean; status: number; data: T | any; response: Response }> {
  const url = apiPath(path);
  const headers: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(init.headers || {}),
    ...getAuthHeaders(),
  };
  const opts: RequestInit = {
    credentials: "include",
    ...init,
    headers,
    body:
      init.json !== undefined
        ? JSON.stringify(init.json)
        : init.body !== undefined
        ? init.body
        : undefined,
  };
  const response = await fetch(url, opts);
  const ct = response.headers.get("content-type") || "";
  const data = ct.includes("application/json")
    ? await response.json().catch(() => ({}))
    : await response.text().catch(() => "");
  return { ok: response.ok, status: response.status, data, response };
}

/* -------------------------------------------------------
 * مُساعدات صغيرة إضافية (اختيارية)
 * ----------------------------------------------------- */


/** اختبار سريع للاتصال/المصادقة. */
export async function pingApi(): Promise<{ ok: boolean; status: number }> {
  try {
    const { response, status, ok } = await jsonFetch("/ping");
    return { ok, status: status || response.status };
  } catch {
    return { ok: false, status: 0 };
  }
}
